import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import produce from 'immer';
import { CurrencyInput, IntegerInput } from '..';
import { possessive, calcPredictedLtcEndAge, isNullOrUndefined, SessionType } from '@shared';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import {
  selectClient,
  putFundingSourcesForClientByClientIdRequest,
  selectSession,
  selectSessionAdvisor,
} from '@client/model';
import { FormErrorAlert } from './FormErrorAlert';
import { AnnuityField } from './AnnuityField';
import { DialogModal } from './DialogModal';
import { Button } from './ui/button';
import { useSearchParams } from 'react-router-dom';

interface AnnuityDetailsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

type AnnuityKey = keyof AnnuitySource;

const ErrorMessage = ({ message }: { message?: string }) => {
  if (!message) {
    return null;
  } else {
    return <p className="ml-2 text-sm text-red-400">{message}</p>;
  }
};

const requiredKeys: AnnuityKey[] = [
  'annuityPurchasePrice',
  'annuityExpectedPaymentStartAge',
  'annuityAnnualPayOut',
];

export function AnnuityDetailsModal({ open, setOpen }: AnnuityDetailsModalProps) {
  const client = useSelector(selectClient);

  const { sessionType } = useSelector(selectSession);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();
  const {
    fundingSources,
    clientId,
    fundingSources: { annuity },
    clientFirstName,
  } = client;

  const predictedLtcEndAge = Math.ceil(calcPredictedLtcEndAge(client));
  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const [annuityState, setAnnuityState] = useState(
    isAdvisorSession ? { ...annuity, addedByAdvisorId: advisorId } : annuity,
  );
  const [initialAnnuityState, setInitialAnnuityState] = useState(
    isAdvisorSession ? { ...annuity, addedByAdvisorId: advisorId } : annuity,
  );
  const [showWarning, setShowWarning] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);

  const [formErrors, setFormErrors] = useState({} as Record<AnnuityKey, string>);

  useEffect(() => {
    if (open) {
      let openingAnnuityState = annuity ? { ...annuity } : ({} as AnnuitySource);

      if (isAdvisorSession) {
        openingAnnuityState = {
          ...openingAnnuityState,
          addedByAdvisorId: advisorId,
        };
      }

      setAnnuityState(openingAnnuityState);
      setInitialAnnuityState(openingAnnuityState);
    }
  }, [open, annuity]);

  const handleChangeAnnuity = (annuityKey: AnnuityKey) => (value: number) => {
    setAnnuityState(prevAnnuityModalState => {
      return {
        ...prevAnnuityModalState,
        [annuityKey]: value,
      };
    });
  };

  const validateForm = () => {
    let isFormValid = true;

    const errors = {} as Record<AnnuityKey, string>;
    requiredKeys.forEach(key => {
      if (isNullOrUndefined(annuityState[key])) {
        errors[key] = 'Required Field';
      }
    });
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      isFormValid = false;
    }
    return isFormValid;
  };

  const handleClickSubmit = () => {
    const isFormValid = validateForm();
    if (!isFormValid) {
      return;
    }

    const newFundingSources = produce(fundingSources, draftFundingSources => {
      draftFundingSources.annuity = {
        ...annuityState,
        hasAnnuity: true,
      };
    });

    dispatch(
      putFundingSourcesForClientByClientIdRequest({
        clientId: clientId,
        fundingSources: newFundingSources,
      }),
    );

    const newParams = new URLSearchParams(searchParams);
    newParams.set('downloadReport', 'true');
    setSearchParams(newParams, { replace: true });
    setOpen(false);
  };

  const formIsValid = Object.keys(formErrors).length === 0;

  const resetForm = () => {
    setAnnuityState(annuity ?? null);
    setFormErrors({} as Record<AnnuityKey, string>);
  };

  const onClose = () => {
    if (JSON.stringify(annuityState) !== JSON.stringify(initialAnnuityState)) {
      setShowWarning(true);
    } else {
      setOpen(false);
      resetForm();
    }
  };

  const handleConfirmClose = () => {
    setShowWarning(false);
    setOpen(false);
    resetForm();
  };

  const handleCancelClose = () => {
    setShowWarning(false);
    setPendingClose(false);
  };

  return (
    <>
      <UnsavedChangesDialog
        showWarning={showWarning}
        handleCancelClose={handleCancelClose}
        handleConfirmClose={handleConfirmClose}
      />

      <DialogModal
        open={open}
        onClose={onClose}
        title="Annuity Details"
        subTitle="Please provide the following details for the annuity."
        width="md"
        primaryButton={
          <Button
            type="button"
            className="mb-14 px-12 py-2 text-lg font-semibold text-white sm:mb-0 sm:ml-3 sm:w-auto"
            onClick={handleClickSubmit}
          >
            Submit
          </Button>
        }
      >
        <div className="relative pb-8">
          <AnnuityField
            title="Purchase Price (Premium)"
            description="The lump sum amount paid to purchase the annuity."
          >
            <CurrencyInput
              label="PURCHASE PRICE"
              placeholder="Enter dollars"
              value={annuityState.annuityPurchasePrice ?? null}
              onChange={handleChangeAnnuity('annuityPurchasePrice')}
              onPreview={handleChangeAnnuity('annuityPurchasePrice')}
            />
            <ErrorMessage message={formErrors.annuityPurchasePrice} />
          </AnnuityField>

          <AnnuityField
            title="Expected Payment Start Age"
            description="The expected age at which the annuity payouts will begin."
          >
            <IntegerInput
              unit="years old"
              label="PAYMENT START AGE"
              placeholder="Enter years"
              value={annuityState.annuityExpectedPaymentStartAge ?? null}
              onChange={handleChangeAnnuity('annuityExpectedPaymentStartAge')}
              onPreview={handleChangeAnnuity('annuityExpectedPaymentStartAge')}
            />
            <ErrorMessage message={formErrors.annuityExpectedPaymentStartAge} />
          </AnnuityField>

          <AnnuityField
            title="Annual Payout"
            description="The estimated or guaranteed lifetime annual payout of the annuity. For greater accuracy, estimate the net amount after fees and taxes."
          >
            <CurrencyInput
              label="PAYMENT AMOUNT"
              placeholder="Enter dollars"
              value={annuityState.annuityAnnualPayOut ?? null}
              onChange={handleChangeAnnuity('annuityAnnualPayOut')}
              onPreview={handleChangeAnnuity('annuityAnnualPayOut')}
            />
            <ErrorMessage message={formErrors.annuityAnnualPayOut} />
          </AnnuityField>

          <AnnuityField
            title="Annual Payout for Long-Term Care"
            description="For annuities with long-term care features, the annual payout increases when the annuitant requires long-term care. This enhanced payout replaces the standard annual amount for the duration of the eligible period. For greater accuracy, estimate the net amount after fees and taxes."
          >
            <div className="space-y-4">
              <CurrencyInput
                label="PAYMENT AMOUNT"
                placeholder="Enter dollars"
                value={annuityState.annuityAnnualPayoutForLtc ?? null}
                onChange={handleChangeAnnuity('annuityAnnualPayoutForLtc')}
                onPreview={handleChangeAnnuity('annuityAnnualPayoutForLtc')}
              />
              <IntegerInput
                unit="years"
                label="PAYMENT PERIOD"
                placeholder="Enter years"
                value={annuityState.annuityAnnualPayoutForLtcPeriodYears ?? null}
                onChange={handleChangeAnnuity('annuityAnnualPayoutForLtcPeriodYears')}
                onPreview={handleChangeAnnuity('annuityAnnualPayoutForLtcPeriodYears')}
              />
            </div>
          </AnnuityField>

          <AnnuityField
            title="Expected Payment End Age"
            description={`The expected age at which the annuity payouts will end. This value is set to the expected end year of ${possessive(clientFirstName)} long-term care and cannot be modified at this time.`}
            isLastField={true}
          >
            <IntegerInput
              unit="years old"
              label="PAYMENT END AGE"
              placeholder="Enter years"
              disabled={true}
              value={predictedLtcEndAge}
              onChange={() => {}}
            />
          </AnnuityField>

          {!formIsValid && (
            <FormErrorAlert mainErrorMessage="Please fill out all required fields to continue." />
          )}
        </div>
      </DialogModal>
    </>
  );
}
